import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL, getToken } from "../../shared/utils.js/utils";
import i18n from "../../i18n/i18n";

// Get Customers
export const getCustomers = createAsyncThunk(
  "Customer/getCustomers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      if (
        params.is_active !== undefined &&
        params.is_active !== null &&
        params.is_active !== ""
      ) {
        queryParams.append("is_active", params.is_active);
      }
      const url = `${BASE_URL}/customers${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Get Customer Details
export const getCustomerDetails = createAsyncThunk(
  "Customer/getCustomerDetails",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.get(`${BASE_URL}/customers/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Add Customer
export const addCustomer = createAsyncThunk(
  "Customer/addCustomer",
  async (data, { rejectWithValue }) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.post(`${BASE_URL}/customers`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Update Customer
export const updateCustomer = createAsyncThunk(
  "Customer/updateCustomer",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.put(`${BASE_URL}/customers/${id}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Toggle Customer Status
// Uses POST to avoid CORS "Method PATCH is not allowed" when server does not allow PATCH in Access-Control-Allow-Methods.
// URL: /customers/:id/toggle-status , method: POST (backend must accept POST for this route).
export const toggleCustomerStatus = createAsyncThunk(
  "Customer/toggleCustomerStatus",
  async (arg, { rejectWithValue }) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const customerId = typeof arg === "object" && arg !== null ? arg.id : arg;
      const url = `${BASE_URL}/customers/${customerId}/toggle-status`;
      const headers = {
        Authorization: `Bearer ${token}`,
        "Accept-Language": i18n.language,
        Accept: "application/json",
        "Content-Type": "application/json",
      };
      // Prefer POST (CORS-safe); fallback to PATCH if backend only accepts PATCH
      let response;
      try {
        response = await axios.post(url, {}, { headers });
      } catch (postError) {
        if (postError.response && postError.response.status === 405) {
          response = await axios.patch(url, {}, { headers });
        } else {
          throw postError;
        }
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Delete Customer
export const deleteCustomer = createAsyncThunk(
  "Customer/deleteCustomer",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.delete(`${BASE_URL}/customers/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);
